import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  tableCellClasses,
  CircularProgress,
  Stack,
  Fab,
  Fade,
  LinearProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchExpenseCategoryReport, downloadExpenseCategoryReportExcel } from '../../store/reportStore/reportThunk';
import ReusableReportHeader from '../../components/Reports/ReusableReportHeader';
import Filters from '../../components/Filters/Filters';
import { toast } from 'react-toastify';
import styled_ from "@emotion/styled";
import { saveAs } from 'file-saver';
import { Utils } from '../../utils/common-utils';
import moment from 'moment';

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#eaecf0',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: '9pt',
    paddingLeft: '15px !important',
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: '0.75px solid #d9d4d4',
    paddingLeft: '15px !important',
    margin: 0,
    backgroundColor: '#fff',
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  '&:last-child td, &:last-child th': {},
}));

interface PaymentModeBreakdown {
  [key: string]: {
    count: number;
    amount: number;
  };
}

interface CategoryData {
  categoryId: string;
  categoryName: string;
  categoryCode: string;
  categoryColor: string;
  transactionCount: number;
  totalAmount: number;
  totalTDS: number;
  totalNetAmount: number;
  avgTransactionValue: number;
  percentageOfTotal: number;
  paymentModeBreakdown: PaymentModeBreakdown;
  vendorCount: number;
  firstTransactionDate: string;
  lastTransactionDate: string;
}

interface Summary {
  totalExpenses: string;
  totalCategories: number;
  avgExpensePerCategory: string;
  highestSpendingCategory: {
    name: string;
    amount: number;
    percentage: number;
  } | null;
  lowestSpendingCategory: {
    name: string;
    amount: number;
    percentage: number;
  } | null;
  totalTransactions: number;
  totalTDS: string;
  totalNetAmount: string;
}

interface ExpenseCategoryReportData {
  categories: CategoryData[];
  summary: Summary;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    limit: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

const ExpenseCategoryReportPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector((state) => state.auth);
  const accountId = selectedAccount?._id;

  const [filters, setFilters] = useState('');
  const [filterData, setFilterData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState<ExpenseCategoryReportData | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Scroll button states
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('down');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTop = useRef(0);

  // Fetch Expense Category Report
  const fetchReport = (pageNum: number = 1, search: string = searchTerm, append: boolean = false) => {
    if (!accountId || !filters) return;

    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    let queryParams = `${filters}&page=${pageNum}&limit=50`;

    // Add search filter
    if (search.trim()) {
      queryParams += `&search=${encodeURIComponent(search.trim())}`;
    }

    dispatch(fetchExpenseCategoryReport({ accountId, params: queryParams }))
      .unwrap()
      .then((response) => {
        if (response.success) {
          if (append && reportData) {
            // Append new categories to existing ones
            setReportData({
              ...response.data,
              categories: [...reportData.categories, ...response.data.categories]
            });
          } else {
            setReportData(response.data);
          }

          // Check if there's more data to load
          setHasMore(response.data.pagination.hasNextPage);
        } else {
          toast.error('Failed to fetch Expense Category report');
        }
      })
      .catch((error) => {
        toast.error(error.message || 'Failed to fetch Expense Category report');
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadingMore(false);
      });
  };

  // Fetch report when filters change
  useEffect(() => {
    if (filters) {
      setPage(1);
      fetchReport(1, searchTerm, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const filterOnChangeHandler = (data: any) => {
    setFilters(data);
    setPage(1);
  };

  const filterdOnChangeHandler = (data: any) => {
    setFilterData(data);
  };

  // Debounced search - triggers API call 500ms after user stops typing
  useEffect(() => {
    if (!filters) return;

    const timeoutId = setTimeout(() => {
      setPage(1);
      fetchReport(1, searchTerm, false);
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleDownloadExcel = () => {
    if (!accountId || !filters) {
      toast.warn('Please select date range first');
      return;
    }

    let queryParams = `${filters}&isDownloadExcel=true`;

    if (searchTerm.trim()) {
      queryParams += `&search=${encodeURIComponent(searchTerm.trim())}`;
    }

    dispatch(downloadExpenseCategoryReportExcel({ accountId, params: queryParams }))
      .unwrap()
      .then((blob) => {
        const fileName = `Expense_Category_Report_${new Date().getTime()}.xlsx`;
        saveAs(blob, fileName);
        toast.success('Excel file downloaded successfully');
      })
      .catch((error) => {
        toast.error(error.message || 'Failed to download Excel file');
      });
  };

  // Handle scroll event
  const handleScroll = () => {
    if (!scrollContainerRef.current) return;

    const currentScrollTop = scrollContainerRef.current.scrollTop;
    const scrollHeight = scrollContainerRef.current.scrollHeight;
    const clientHeight = scrollContainerRef.current.clientHeight;

    // Determine scroll direction
    if (currentScrollTop > lastScrollTop.current) {
      setScrollDirection('down');
    } else if (currentScrollTop < lastScrollTop.current) {
      setScrollDirection('up');
    }

    lastScrollTop.current = currentScrollTop;

    // Show button when scrolling
    if (currentScrollTop > 100 || currentScrollTop < scrollHeight - clientHeight - 100) {
      setShowScrollButton(true);

      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Hide button after 2 seconds of no scrolling
      scrollTimeoutRef.current = setTimeout(() => {
        setShowScrollButton(false);
      }, 2000);
    }

    // Infinite scroll - load more when near bottom
    const scrollBottom = scrollHeight - currentScrollTop - clientHeight;
    if (scrollBottom < 300 && !isLoadingMore && reportData?.pagination?.hasNextPage) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchReport(nextPage, searchTerm, true);
    }
  };

  // Handle scroll button click
  const handleScrollButtonClick = () => {
    if (!scrollContainerRef.current) return;

    const scrollHeight = scrollContainerRef.current.scrollHeight;

    if (scrollDirection === 'down') {
      // Scroll to bottom
      scrollContainerRef.current.scrollTo({
        top: scrollHeight,
        behavior: 'smooth'
      });
    } else {
      // Scroll to top
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Get payment mode chip color
  const getPaymentModeColor = (mode: string) => {
    switch (mode) {
      case 'CASH':
        return '#10b981';
      case 'CHEQUE':
        return '#3b82f6';
      case 'UPI':
        return '#8b5cf6';
      case 'NETBANKING':
        return '#06b6d4';
      case 'NEFT':
      case 'RTGS':
        return '#0891b2';
      default:
        return '#6b7280';
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ width: 'calc(100% - 7px)', height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', mb: 2, position: 'relative' }} className={'c-card2'}>
          {/* Scrollable content area */}
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}>
            {/* Report Header */}
            <ReusableReportHeader
              reportType="EXPENSE_CATEGORY"
              title="Expense Category Report"
              subtitle={(filterData && filterData.periodString) ? filterData.periodString : 'Analytical Expense Breakdown by Category'}
              recordCount={reportData?.summary?.totalCategories || 0}
              isLoading={isLoading}
              onDownloadPDF={handleDownloadExcel}
              showPrint={false}
              showShare={false}
            />

            {/* Filters and Summary Section */}
            <Grid container px={2} py={1.5} alignItems="flex-start">
              {/* Left side - Date Filter and Search */}
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Filters
                    type="report"
                    filterHandler={filterOnChangeHandler}
                    selectedFilters={filterdOnChangeHandler}
                    hideFilter={true}
                    hidePartySearch={true}
                  />
                  <TextField
                    size="small"
                    placeholder="Search Category Name, Code..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                      minWidth: '300px',
                      border: "3px solid #e9ecf1",
                      boxShadow: 'none',
                      borderRadius: '10px',
                      ml: '10px !important',
                      '& .MuiOutlinedInput-root': {
                        padding: '8px 14px',
                        alignItems: 'center',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: 0,
                        margin: 0,
                        height: 'auto',
                        lineHeight: 'normal',
                      },
                      '& .MuiInputAdornment-root': {
                        marginRight: '8px',
                      },
                      "& .MuiOutlinedInput-notchedOutline": { border: 0 },
                      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                        border: 0,
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: 0,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {/* <SearchIcon color="action" /> */}
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <ClearIcon
                            color="action"
                            sx={{ cursor: 'pointer', fontSize: '20px' }}
                            onClick={() => setSearchTerm('')}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </Grid>

              {/* Right side summary section */}
              <Grid item xs={12} md={6} style={{ textAlign: 'right', paddingLeft: '16px' }}>
                {reportData?.summary && (
                  <>
                    <Typography style={{ fontSize: 14, fontWeight: 400, marginBottom: '6px' }}>
                      <strong style={{ paddingRight: 10 }}>Total Categories:</strong>
                      {reportData.summary.totalCategories}
                    </Typography>
                    <Typography style={{
                      fontSize: 14,
                      fontWeight: 400,
                      marginBottom: '6px',
                      color: '#dc2626'
                    }}>
                      <strong style={{ paddingRight: 10 }}>Total Expenses:</strong>
                      {Utils.formatNumber(parseFloat(reportData.summary.totalExpenses || '0').toFixed(2))}
                    </Typography>
                    <Typography style={{
                      fontSize: 14,
                      fontWeight: 400,
                      marginBottom: '6px',
                      color: '#6b7280'
                    }}>
                      <strong style={{ paddingRight: 10 }}>Avg per Category:</strong>
                      {Utils.formatNumber(parseFloat(reportData.summary.avgExpensePerCategory || '0').toFixed(2))}
                    </Typography>
                    <Typography style={{
                      fontSize: 14,
                      fontWeight: 400,
                      marginBottom: '6px',
                      // color: '#f59e0b'
                    }}>
                      <strong style={{ paddingRight: 10 }}>Total TDS:</strong>
                      {Utils.formatNumber(parseFloat(reportData.summary.totalTDS || '0').toFixed(2))}
                    </Typography>
                    
                    {reportData.summary.highestSpendingCategory && (
                      <Typography style={{
                        fontSize: 14,
                        fontWeight: 400,
                        marginBottom: '6px',
                        // color: '#dc2626'
                      }}>
                        <strong style={{ paddingRight: 10 }}>Highest Spending Category:</strong>
                        {reportData.summary.highestSpendingCategory.name} - {Utils.formatNumber(reportData.summary.highestSpendingCategory.amount.toFixed(2))}({reportData.summary.highestSpendingCategory.percentage.toFixed(2)}%)
                      </Typography>
                    )}
                    
                    {reportData.summary.lowestSpendingCategory && (
                      <Typography style={{
                        fontSize: 14,
                        fontWeight: 400,
                        marginBottom: '6px',
                        // color: '#dc2626'
                      }}>
                        <strong style={{ paddingRight: 10 }}>Lowest Spending Category:</strong>
                        {reportData.summary.lowestSpendingCategory.name} - {Utils.formatNumber(reportData.summary.lowestSpendingCategory.amount.toFixed(2))}({reportData.summary.lowestSpendingCategory.percentage.toFixed(2)}%)
                      </Typography>
                    )}

                  </>
                )}
              </Grid>

            </Grid>

            {!isLoading ? (
              <>
                <Table sx={{ minWidth: 700, tableLayout: 'fixed', width: '100%' }} size='small' aria-label="customized table">
                  <colgroup>
                    <col style={{ width: '3%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '8%' }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>Category Name</StyledTableCell>
                      <StyledTableCell>Code</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'center' }}>Txns</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'right' }}>Total Amount</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'right' }}>% of Total</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'right' }}>Avg/Txn</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'right' }}>TDS</StyledTableCell>
                      <StyledTableCell>Payment Modes</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'center' }}>Vendors</StyledTableCell>
                      <StyledTableCell style={{ textAlign: 'center' }}>Period</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(reportData && reportData.categories && reportData.categories.length > 0) ? reportData.categories.map((cat, index) => (
                      <StyledTableRow key={`${cat.categoryId}-${index}`}>
                        <StyledTableCell style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              backgroundColor: cat.categoryColor,
                              margin: 'auto'
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell style={{ verticalAlign: 'middle', fontWeight: '600' }}>
                          <Typography variant="body2" style={{ fontSize: '9pt', fontWeight: '600' }}>
                            {cat.categoryName}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell style={{ verticalAlign: 'middle' }}>
                          <Chip
                            label={cat.categoryCode}
                            size="small"
                            variant="outlined"
                            style={{
                              fontSize: '7pt',
                              height: '18px',
                              borderColor: cat.categoryColor,
                              color: cat.categoryColor
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell style={{ textAlign: 'center', verticalAlign: 'middle', fontWeight: '500' }}>
                          {cat.transactionCount}
                        </StyledTableCell>
                        <StyledTableCell style={{
                          textAlign: 'right',
                          fontWeight: '700',
                          color: '#dc2626',
                          verticalAlign: 'middle'
                        }}>
                          {Utils.formatNumber(cat.totalAmount.toFixed(2))}
                        </StyledTableCell>
                        <StyledTableCell style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            <Typography variant="body2" style={{ fontSize: '9pt', fontWeight: '600' }}>
                              {cat.percentageOfTotal.toFixed(2)}%
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(cat.percentageOfTotal, 100)}
                              sx={{
                                width: 40,
                                height: 4,
                                borderRadius: 2,
                                backgroundColor: '#e5e7eb',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: cat.categoryColor
                                }
                              }}
                            />
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell style={{
                          textAlign: 'right',
                          fontWeight: '500',
                          verticalAlign: 'middle',
                          color: '#6b7280'
                        }}>
                          {Utils.formatNumber(cat.avgTransactionValue.toFixed(2))}
                        </StyledTableCell>
                        <StyledTableCell style={{
                          textAlign: 'right',
                          fontWeight: cat.totalTDS > 0 ? '600' : 'normal',
                          color: cat.totalTDS > 0 ? '#f59e0b' : '#9ca3af',
                          verticalAlign: 'middle'
                        }}>
                          {cat.totalTDS > 0 ? `${Utils.formatNumber(cat.totalTDS.toFixed(2))}` : '-'}
                        </StyledTableCell>
                        <StyledTableCell style={{ verticalAlign: 'middle' }}>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {Object.keys(cat.paymentModeBreakdown).map((mode) => (
                              <Chip
                                key={mode}
                                label={`${mode} (${cat.paymentModeBreakdown[mode].count})`}
                                size="small"
                                style={{
                                  fontSize: '7pt',
                                  height: '18px',
                                  backgroundColor: getPaymentModeColor(mode),
                                  color: '#fff',
                                  marginBottom: '2px'
                                }}
                              />
                            ))}
                          </Stack>
                        </StyledTableCell>
                        <StyledTableCell style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <Chip
                            label={cat.vendorCount}
                            size="small"
                            style={{
                              fontSize: '8pt',
                              height: '20px',
                              backgroundColor: '#e0e7ff',
                              color: '#4f46e5'
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell style={{ verticalAlign: 'middle' }}>
                          <Typography variant="caption" display="block" style={{ fontSize: '7pt', color: '#6b7280' }}>
                            {moment(cat.firstTransactionDate).format('DD-MMM')}
                          </Typography>
                          <Typography variant="caption" display="block" style={{ fontSize: '7pt', color: '#6b7280' }}>
                            to {moment(cat.lastTransactionDate).format('DD-MMM')}
                          </Typography>
                        </StyledTableCell>
                      </StyledTableRow>
                    )) : (
                      <StyledTableRow>
                        <StyledTableCell colSpan={11} style={{ textAlign: 'center' }}>No Data Found</StyledTableCell>
                      </StyledTableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Loading More Indicator */}
                {isLoadingMore && (
                  <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress size={30} color="primary" />
                    <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                      Loading more categories...
                    </Typography>
                  </Box>
                )}

                {/* No More Data Info */}
                {reportData && !reportData.pagination.hasNextPage && reportData.categories.length > 0 && (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      Showing all {reportData.pagination.totalRecords} categories
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Grid container p={2}>
                <Grid item xs={12} style={{ textAlign: 'center' }}>
                  <CircularProgress color="info" />
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Fixed Total Row */}
          {!isLoading && reportData?.summary && (
            <Box>
              <Table sx={{ minWidth: 700, tableLayout: 'fixed', width: '100%' }} size='small'>
                <colgroup>
                  <col style={{ width: '3%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '8%' }} />
                </colgroup>
                <TableBody>
                  <TableRow style={{ backgroundColor: '#f1f5f9' }}>
                    <StyledTableCell colSpan={4} style={{
                      fontWeight: 'bold',
                      fontSize: '11pt',
                      backgroundColor: '#f1f5f9',
                      color: '#1e293b',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      padding: 5,
                      paddingLeft: '15px !important'
                    }}>
                      TOTAL ({reportData.summary.totalCategories} Categories)
                    </StyledTableCell>
                    <StyledTableCell style={{
                      textAlign: 'right',
                      fontWeight: 'bold',
                      fontSize: '10pt',
                      color: '#dc2626',
                      backgroundColor: '#f1f5f9',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      padding: 5,
                      paddingRight: '15px !important'
                    }}>
                      {Utils.formatNumber(parseFloat(reportData.summary.totalExpenses).toFixed(2))}
                    </StyledTableCell>
                    <StyledTableCell style={{
                      textAlign: 'right',
                      fontWeight: 'bold',
                      fontSize: '10pt',
                      color: '#1e293b',
                      backgroundColor: '#f1f5f9',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      padding: 5
                    }}>
                      100%
                    </StyledTableCell>
                    <StyledTableCell style={{
                      textAlign: 'right',
                      fontWeight: 'bold',
                      fontSize: '10pt',
                      color: '#6b7280',
                      backgroundColor: '#f1f5f9',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      padding: 5,
                      paddingRight: '15px !important'
                    }}>
                      {Utils.formatNumber(parseFloat(reportData.summary.avgExpensePerCategory).toFixed(2))}
                    </StyledTableCell>
                    <StyledTableCell style={{
                      textAlign: 'right',
                      fontWeight: 'bold',
                      fontSize: '10pt',
                      color: '#f59e0b',
                      backgroundColor: '#fff7ed',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      borderLeft: '1px solid #f59e0b',
                      borderRight: '1px solid #f59e0b',
                      borderBottom: '1px solid #f59e0b',
                      padding: 5,
                      paddingRight: '15px !important'
                    }}>
                      {Utils.formatNumber(parseFloat(reportData.summary.totalTDS).toFixed(2))}
                    </StyledTableCell>
                    <StyledTableCell colSpan={3} style={{
                      backgroundColor: '#f1f5f9',
                      border: '0.75px solid #d9d4d4',
                      borderTop: '2px solid #64748b',
                      padding: 5
                    }}>
                    </StyledTableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Scroll to Top/Bottom Button */}
          <Fade in={showScrollButton}>
            <Fab
              color="primary"
              size="medium"
              onClick={handleScrollButtonClick}
              sx={{
                position: 'absolute',
                bottom: 46,
                right: 16,
                zIndex: 1000,
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.3s ease-in-out'
              }}
              aria-label={scrollDirection === 'down' ? 'scroll to bottom' : 'scroll to top'}
            >
              {scrollDirection === 'down' ? <ArrowDownIcon /> : <ArrowUpIcon />}
            </Fab>
          </Fade>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ExpenseCategoryReportPage;
